import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { AccessTime as IdleIcon, Person as PersonIcon, Search as SearchIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const EmpIdleTimeReport = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [period, setPeriod] = useState('weekly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/reports/users');
      const userList = response.data?.data || [];
      setUsers(userList);
    } catch (error) {
      console.error('Failed to fetch report users', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const generateReport = useCallback(async () => {
    if (!selectedUser) {
      toast.error('Select an employee to run this report.');
      return;
    }
    setGenerating(true);
    try {
      const params = new URLSearchParams({
        user_id: selectedUser,
        period
      });
      if (period === 'custom') {
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
      }
      const response = await api.get(`/reports/idle-time?${params.toString()}`);
      setReport(response.data?.data || null);
    } catch (error) {
      console.error('Failed to generate idle time report', error);
      const msg = error.response?.data?.message || 'Failed to generate idle time report.';
      toast.error(msg);
      setReport(null);
    } finally {
      setGenerating(false);
    }
  }, [selectedUser, period, startDate, endDate]);

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      : '-';

  const formatTime = (value) =>
    value
      ? new Date(value).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        })
      : '-';

  const summaryCards = useMemo(() => {
    if (!report?.summary) return [];
    return [
      { label: 'Idle Events', value: report.summary.idleEvents },
      { label: 'Total Idle Minutes', value: report.summary.totalIdleMinutes },
      { label: 'Total Idle Hours', value: report.summary.totalIdleHours },
      { label: 'Longest Idle (mins)', value: report.summary.longestIdleMinutes }
    ];
  }, [report]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IdleIcon sx={{ fontSize: 32 }} />
          Emp Idle Time (Confidential)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose any employee and date range. Requires confidential reports access.
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Employee</InputLabel>
                <Select value={selectedUser} label="Employee" onChange={(e) => setSelectedUser(e.target.value)}>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value)}>
                  <MenuItem value="daily">Today</MenuItem>
                  <MenuItem value="weekly">This Week</MenuItem>
                  <MenuItem value="monthly">This Month</MenuItem>
                  <MenuItem value="yearly">This Year</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {period === 'custom' && (
              <>
                <Grid item xs={12} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={period === 'custom' ? 2 : 6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={generating ? <CircularProgress size={16} /> : <SearchIcon />}
                disabled={!selectedUser || generating}
                onClick={() => void generateReport()}
              >
                {generating ? 'Loading...' : 'Generate'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {report && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Employee
                  </Typography>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    {report.user?.name || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {report.user?.email || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {report.dateRange?.start} to {report.dateRange?.end}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {summaryCards.map((card) => (
              <Grid key={card.label} item xs={12} sm={6} md={2}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      {card.label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {card.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Idle From</TableCell>
                  <TableCell>Idle To</TableCell>
                  <TableCell align="right">Idle Mins</TableCell>
                  <TableCell align="right">Timeout Mins</TableCell>
                  <TableCell>Source Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(report.occurrences || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No idle events for the selected filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  report.occurrences.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{formatDate(row.idle_to)}</TableCell>
                      <TableCell>{formatTime(row.idle_from)}</TableCell>
                      <TableCell>{formatTime(row.idle_to)}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={row.idle_minutes} color={row.idle_minutes >= 30 ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell align="right">{row.timeout_minutes}</TableCell>
                      <TableCell>
                        {row.activity ? `${row.activity.module || '-'} / ${row.activity.action || '-'}` : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default EmpIdleTimeReport;
